'use client'

import { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faUserPlus, 
  faHeadset, 
  faLifeRing, 
  faFlask,
  faListCheck,
  faCookie,
  faGear,
  faArrowRightFromBracket,
  faChevronDown,
  faUpRightFromSquare,
  faMoon,
  faSun,
  faCompress
} from '@fortawesome/free-solid-svg-icons'

interface UserDropdownProps {
  user: {
    name: string
    email: string
    avatar: string
    newVersion?: boolean
    admin?: number | string | boolean | null
  }
}

type PanelTheme = 'theme-default' | 'theme-001' | 'theme-002-executive'
type PanelMode = 'mode-light' | 'mode-dark'
type PanelLayout = 'layout-default' | 'layout-001-compact'
type PanelFont = string

type PanelAppearance = {
  theme: PanelTheme
  mode: PanelMode
  layout: PanelLayout
  font: PanelFont
}

declare global {
  interface Window {
    FarktorPanelAppearance?: {
      get: () => PanelAppearance
      setTheme: (theme: PanelTheme) => void
      setMode: (mode: PanelMode) => void
      setLayout: (layout: PanelLayout) => void
      setFont: (font: PanelFont) => void
      reset: () => void
    }
  }
}

const defaultAppearance: PanelAppearance = {
  theme: 'theme-default',
  mode: 'mode-light',
  layout: 'layout-default',
  font: 'font-default',
}

const panelFontOptions = [
  { value: 'font-default', label: 'Default' },
  { value: 'font-roboto', label: 'Roboto' },
  { value: 'font-open-sans', label: 'Open Sans' },
  { value: 'font-lato', label: 'Lato' },
  { value: 'font-montserrat', label: 'Montserrat' },
  { value: 'font-poppins', label: 'Poppins' },
  { value: 'font-nunito', label: 'Nunito' },
  { value: 'font-source-sans-3', label: 'Source Sans 3' },
  { value: 'font-work-sans', label: 'Work Sans' },
  { value: 'font-rubik', label: 'Rubik' },
  { value: 'font-mulish', label: 'Mulish' },
  { value: 'font-manrope', label: 'Manrope' },
  { value: 'font-ibm-plex-sans', label: 'IBM Plex Sans' },
  { value: 'font-dm-sans', label: 'DM Sans' },
  { value: 'font-plus-jakarta-sans', label: 'Plus Jakarta Sans' },
  { value: 'font-outfit', label: 'Outfit' },
  { value: 'font-urbanist', label: 'Urbanist' },
  { value: 'font-noto-sans', label: 'Noto Sans' },
  { value: 'font-raleway', label: 'Raleway' },
  { value: 'font-lexend', label: 'Lexend' },
  { value: 'font-quicksand', label: 'Quicksand' },
]

function readPanelAppearance(): PanelAppearance {
  if (typeof window === 'undefined') return defaultAppearance
  return window.FarktorPanelAppearance?.get() || defaultAppearance
}

export function UserDropdown({ user }: UserDropdownProps) {
  const router = useRouter()
  const isAdminUser = Number(user.admin || 0) === 1
  const [isOpen, setIsOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [appearance, setAppearance] = useState<PanelAppearance>(defaultAppearance)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setAppearance(readPanelAppearance())
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const setTheme = (theme: PanelTheme) => {
    window.FarktorPanelAppearance?.setTheme(theme)
    setAppearance(readPanelAppearance())
  }

  const setMode = (mode: PanelMode) => {
    window.FarktorPanelAppearance?.setMode(mode)
    setAppearance(readPanelAppearance())
  }

  const setLayout = (layout: PanelLayout) => {
    window.FarktorPanelAppearance?.setLayout(layout)
    setAppearance(readPanelAppearance())
  }

  const setFont = (font: PanelFont) => {
    window.FarktorPanelAppearance?.setFont(font)
    setAppearance(readPanelAppearance())
  }

  const handleLogout = async () => {
    const token = localStorage.getItem('auth_token')

    try {
      if (token) {
        await fetch('https://app.canerozen.com/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include'
        })
      }
    } catch (error) {
      console.error('Logout request failed:', error)
    } finally {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('admin_store_nav_scope')
      setIsOpen(false)
      router.replace('/login')
      router.refresh()
    }
  }

  const logoutMenuItem = {
    icon: faArrowRightFromBracket,
    label: 'Çıkış',
    onClick: handleLogout,
    external: false,
    danger: true,
  }

  const menuItems = isAdminUser ? [
    {
      icon: faUserPlus,
      label: 'Invite Your Team',
      href: '/invite',
      external: false,
    },
    {
      icon: faHeadset,
      label: 'Talk to Sales',
      href: '/sales',
      external: false,
    },
    {
      icon: faLifeRing,
      label: 'Talk to Support',
      href: 'https://support.example.com',
      external: true,
    },
    {
      icon: faFlask,
      label: 'Participate in Research',
      href: 'https://research.example.com',
      external: true,
    },
    {
      type: 'divider' as const,
    },
    {
      icon: faCookie,
      label: 'Cookie Preferences',
      href: '/cookies',
      external: false,
    },
    {
      icon: faListCheck,
      label: 'Kişisel Görev Listesi',
      href: '/pages/users/task',
      external: false,
    },
    {
      icon: faGear,
      label: 'Settings',
      onClick: () => setSettingsOpen((value) => !value),
      external: false,
      settings: true,
    },
    {
      type: 'divider' as const,
    },
    logoutMenuItem,
  ] : [logoutMenuItem]

  return (
    <div className="user-dropdown" ref={dropdownRef}>
      <button 
        className="user-dropdown__trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="user-dropdown__avatar">
          {user.avatar}
        </div>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`user-dropdown__arrow ${isOpen ? 'user-dropdown__arrow--open' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="user-dropdown__menu">
          {/* User Info Header */}
          <div className="user-dropdown__header">
            <div className="user-dropdown__avatar user-dropdown__avatar--large">
              {user.avatar}
            </div>
            <div className="user-dropdown__info">
              <div className="user-dropdown__name">
                {user.name}
                <span className={`user-dropdown__version-badge ${user.newVersion ? 'user-dropdown__version-badge--new' : 'user-dropdown__version-badge--old'}`}>
                  {user.newVersion ? 'v2' : 'v1'}
                </span>
              </div>
              <div className="user-dropdown__email">{user.email}</div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="user-dropdown__items">
            {menuItems.map((item, index) => {
              if (item.type === 'divider') {
                return <div key={index} className="user-dropdown__divider" />
              }

              const className = `user-dropdown__item ${item.danger ? 'user-dropdown__item--danger' : ''}`
              const content = (
                <>
                  <FontAwesomeIcon icon={item.icon} className="user-dropdown__item-icon" />
                  <span className="user-dropdown__item-label">{item.label}</span>
                  {item.external && (
                    <FontAwesomeIcon
                      icon={faUpRightFromSquare}
                      className="user-dropdown__item-external"
                    />
                  )}
                </>
              )

              if (item.onClick) {
                return (
                  <div key={index}>
                  <button
                    type="button"
                    className={`${className} ${item.settings && settingsOpen ? 'user-dropdown__item--active' : ''}`}
                    onClick={item.onClick}
                    aria-expanded={item.settings ? settingsOpen : undefined}
                  >
                    {content}
                  </button>
                    {item.settings && settingsOpen && (
                      <div className="user-dropdown__appearance" aria-label="Panel görünümü">
                        <div className="user-dropdown__appearance-label">Mod</div>
                        <div className="user-dropdown__appearance-options user-dropdown__appearance-options--two">
                          <button
                            type="button"
                            className={`user-dropdown__appearance-option ${appearance.mode === 'mode-light' ? 'is-active' : ''}`}
                            onClick={() => setMode('mode-light')}
                          >
                            <FontAwesomeIcon icon={faSun} />
                            <span>Açık</span>
                          </button>
                          <button
                            type="button"
                            className={`user-dropdown__appearance-option ${appearance.mode === 'mode-dark' ? 'is-active' : ''}`}
                            onClick={() => setMode('mode-dark')}
                          >
                            <FontAwesomeIcon icon={faMoon} />
                            <span>Dark</span>
                          </button>
                        </div>
                        <div className="user-dropdown__appearance-label">Tema</div>
                        <div className="user-dropdown__appearance-options user-dropdown__appearance-options--two">
                          <button
                            type="button"
                            className={`user-dropdown__appearance-option ${appearance.theme === 'theme-default' ? 'is-active' : ''}`}
                            onClick={() => setTheme('theme-default')}
                          >
                            <FontAwesomeIcon icon={faSun} />
                            <span>Default</span>
                          </button>
                          <button
                            type="button"
                            className={`user-dropdown__appearance-option ${appearance.theme === 'theme-001' ? 'is-active' : ''}`}
                            onClick={() => setTheme('theme-001')}
                          >
                            <FontAwesomeIcon icon={faCompress} />
                            <span>Tema 001</span>
                          </button>
                        </div>
                        <div className="user-dropdown__appearance-label">Font</div>
                        <select
                          className="user-dropdown__font-select"
                          value={appearance.font}
                          onChange={(event) => setFont(event.target.value)}
                          aria-label="Panel fontu"
                        >
                          {panelFontOptions.map((font) => (
                            <option key={font.value} value={font.value}>
                              {font.label}
                            </option>
                          ))}
                        </select>
                        <div className="user-dropdown__appearance-label">Layout</div>
                        <button
                          type="button"
                          className={`user-dropdown__appearance-toggle ${appearance.layout === 'layout-001-compact' ? 'is-active' : ''}`}
                          onClick={() => setLayout(appearance.layout === 'layout-001-compact' ? 'layout-default' : 'layout-001-compact')}
                        >
                          <FontAwesomeIcon icon={faCompress} />
                          <span>Compact layout</span>
                          <span className="user-dropdown__appearance-switch" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <a
                  key={index}
                  href={item.href}
                  className={className}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                >
                  {content}
                </a>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
